import { useCallback, useMemo, useReducer } from "react";

import {
  INITIAL_FILTERS,
  clampRadiusKm,
  clearProducts as clearProductFilters,
  countActiveFilters,
  filtersEqual,
  hasProductFilters,
  normaliseFilters,
} from "./supplierFilters";

const reducer = (state, action) => {
  if (action.type === "apply") {
    const next = normaliseFilters(action.filters);

    if (filtersEqual(state.filters, next)) return state;

    return { filters: next, revision: state.revision + 1 };
  }

  if (action.type === "reset") {
    return { filters: { ...INITIAL_FILTERS }, revision: state.revision + 1 };
  }

  return state;
};

const useSupplierFilters = (initialFilters) => {
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    filters: normaliseFilters(initialFilters),
    revision: 0,
  }));

  const apply = useCallback((filters) => {
    dispatch({ type: "apply", filters });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  const { filters, revision } = state;

  const setRadius = useCallback(
    (radiusKm) => apply({ ...filters, radiusKm: clampRadiusKm(radiusKm) }),
    [apply, filters]
  );

  const clearProducts = useCallback(
    () => apply(clearProductFilters(filters)),
    [apply, filters]
  );

  return useMemo(
    () => ({
      filters,
      revision,
      apply,
      reset,
      setRadius,
      clearProducts,
      activeCount: countActiveFilters(filters),
      hasProductFilters: hasProductFilters(filters),
    }),
    [filters, revision, apply, reset, setRadius, clearProducts]
  );
};

export default useSupplierFilters;
